import React, { FC, useMemo, useState } from "react";
import Input from "../Builder/nodes/Logic/Inputs/Input";
import AddIcon from "../../assets/icons/Icon_Library/application icons/assets/events/add_circle-01.svg2";
import RemoveIcon from "../../assets/icons/Icon_Library/application icons/assets/events/remove-01.svg2";
import { Button, Form, Table } from "react-bootstrap";
import {
  SolutionType,
  useAddProjectToSolutionMutation,
  useGetProjectInterfacesQuery,
  useGetSolutionByUuidQuery,
  useRemoveProjectFromSolutionMutation,
} from "../store/slices/settingsSlice";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";

export const solutionRolesMapping: Record<
  string,
  { label: string; description: string; interface: string[]; for?: string }
> = {
  generic: {
    label: "Generic",
    description: "General purpose component used by the solution.",
    interface: [],
  },
  dependency: {
    label: "Dependency",
    description: "Supporting component required by another component.",
    interface: [],
  },
  "event-create": {
    label: "Event Create",
    description: "Creates or receives the initial event context.",
    interface: ["id"],
    for: "events",
  },
  "add-ticket": {
    label: "Add Ticket",
    description: "Creates a ticket linked to the current event.",
    interface: ["id"],
    for: "events",
  },
  "create-update": {
    label: "Create an Update",
    description: "Posts a message update against the current event.",
    interface: ["id", "message"],
    for: "events",
  },
  "update-event": {
    label: "Update Event",
    description:
      "Updates event details such as name, description, and severity.",
    interface: ["id", "name", "description", "severity"],
    for: "events",
  },
  "create-communication": {
    label: "Create Communication",
    description: "Starts an outbound communication for the current event.",
    interface: ["id", "message"],
    for: "events",
  },
  "context-project": {
    label: "Context Project",
    description: "Provides extra context for the current event.",
    interface: ["id"],
    for: "events",
  },
  "close-event": {
    label: "Close Event",
    description: "Closes or resolves the current event.",
    interface: ["id"],
    for: "events",
  },
  "inbound-message": {
    label: "Inbound Message",
    description: "Handles incoming messages with sender and message content.",
    interface: ["from", "message"],
  },
  "inbound-api": {
    label: "Inbound API",
    description: "Accepts inbound API requests for the solution.",
    interface: [],
  },
  "ping-monitor": {
    label: "Ping Monitor",
    description: "Processes ping monitor checks and response status values.",
    interface: ["url", "status", "response_code"],
  },
  "weather-monitor": {
    label: "Weather Monitor",
    description:
      "Processes weather monitor alerts with short and long messages.",
    interface: ["id", "short_message", "long_message"],
  },
};

interface SelectSolutionProjectProps {
  solution: SolutionType;
  setSolution: (solution: SolutionType) => void;
}

interface ProjectInterface {
  Label: string;
  UUID: string;
  InputArguments?: Array<{
    Role?: string | null;
  }>;
}

interface SupportedRole {
  value: string;
  label: string;
  description: string;
  interfaceVars: Array<{
    supported: boolean;
    value: string;
  }>;
  supported: boolean;
  alreadyAdded: boolean;
}

interface ProjectSupportedRoleSummary {
  value: string;
  label: string;
  description: string;
  alreadyAdded: boolean;
}

const SelectSolutionProject: FC<SelectSolutionProjectProps> = ({
  solution: initialSolution,
  setSolution,
}) => {
  const [selectedProject, setSelectedProject] = useState<string>("");
  const [addProject] = useAddProjectToSolutionMutation();
  const [removeProject] = useRemoveProjectFromSolutionMutation();
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [roleDescriptions, setRoleDescriptions] = useState<
    Record<string, string>
  >({});
  const [adding, setAdding] = useState<boolean>(false);
  const [removing, setRemoving] = useState<boolean>(false);

  const projects =
    (useGetProjectInterfacesQuery({
      for: "solutions",
      limit: 100,
    }).data?.projects as ProjectInterface[] | undefined) || [];

  const solution =
    (useGetSolutionByUuidQuery({ uuid: initialSolution.Uuid }).data as
      | SolutionType
      | undefined) || initialSolution;

  const availableRoles = useMemo(
    () =>
      Object.entries(solutionRolesMapping).filter(([_, { for: kind }]) => {
        if (_ === "dependency") {
          return false;
        }

        if (kind) {
          return kind === initialSolution.Kind;
        }
        return true;
      }),
    [initialSolution.Kind]
  );

  const selectedProjectDetails = useMemo(
    () => projects.find((project) => project.UUID === selectedProject),
    [projects, selectedProject]
  );

  const getProjectSupportedRoles = (
    project: ProjectInterface
  ): ProjectSupportedRoleSummary[] => {
    const inputArgumentRoles = new Set(
      (project.InputArguments || [])
        .map((argument) => argument.Role)
        .filter((inputArgumentRole): inputArgumentRole is string =>
          Boolean(inputArgumentRole)
        )
    );

    return availableRoles
      .filter(
        ([_, { interface: interfaceVars }]) =>
          interfaceVars.length > 0 &&
          interfaceVars.every((interfaceVar) =>
            inputArgumentRoles.has(interfaceVar)
          )
      )
      .map(([value, { label, description }]) => ({
        value,
        label,
        description,
        alreadyAdded:
          solution.Components?.some(
            (component) =>
              component.ItemUuid === project.UUID && component.Role === value
          ) ?? false,
      }));
  };

  const supportedRoles = useMemo<SupportedRole[]>(() => {
    const inputArgumentRoles = new Set(
      (selectedProjectDetails?.InputArguments || [])
        .map((argument) => argument.Role)
        .filter((inputArgumentRole): inputArgumentRole is string =>
          Boolean(inputArgumentRole)
        )
    );

    return availableRoles.map(
      ([value, { label, description, interface: interfaceVars }]) => {
        const requiredInterfaceVars = interfaceVars.map((interfaceVar) => ({
          supported: inputArgumentRoles.has(interfaceVar),
          value: interfaceVar,
        }));

        return {
          value,
          label,
          description,
          interfaceVars: requiredInterfaceVars,
          supported: requiredInterfaceVars.every(
            (interfaceVar) => interfaceVar.supported
          ),
          alreadyAdded: solution.Components?.some(
            (component) =>
              component.ItemUuid === selectedProjectDetails?.UUID &&
              component.Role === value
          ) ?? false,
        };
      }
    );
  }, [availableRoles, selectedProjectDetails, solution.Components]);

  const selectedSupportedRoles = useMemo(
    () =>
      supportedRoles.filter(
        (supportedRole) =>
          supportedRole.supported &&
          !supportedRole.alreadyAdded &&
          selectedRoles.includes(supportedRole.value)
      ),
    [selectedRoles, supportedRoles]
  );

  const defaultRoleDescriptions = useMemo(
    () =>
      supportedRoles.reduce<Record<string, string>>(
        (defaults, supportedRole) => {
          defaults[supportedRole.value] = supportedRole.description;
          return defaults;
        },
        {}
      ),
    [supportedRoles]
  );

  const selectedRoleDescriptionsComplete = useMemo(
    () =>
      selectedSupportedRoles.every((supportedRole) =>
        Boolean(
          (
            roleDescriptions[supportedRole.value] ||
            defaultRoleDescriptions[supportedRole.value]
          )?.trim()
        )
      ),
    [defaultRoleDescriptions, roleDescriptions, selectedSupportedRoles]
  );

  const toggleSelectedRole = (roleValue: string, selected: boolean): void => {
    setSelectedRoles((currentRoles) => {
      if (selected) {
        setRoleDescriptions((currentDescriptions) => ({
          ...currentDescriptions,
          [roleValue]:
            currentDescriptions[roleValue] ||
            defaultRoleDescriptions[roleValue] ||
            "",
        }));

        return currentRoles.includes(roleValue)
          ? currentRoles
          : [...currentRoles, roleValue];
      }

      setRoleDescriptions((currentDescriptions) => {
        const { [roleValue]: _, ...remainingDescriptions } =
          currentDescriptions;
        return remainingDescriptions;
      });

      return currentRoles.filter((currentRole) => currentRole !== roleValue);
    });
  };

  const setSelectedRoleDescription = (
    roleValue: string,
    value: string
  ): void => {
    setRoleDescriptions((currentDescriptions) => ({
      ...currentDescriptions,
      [roleValue]: value,
    }));
  };

  const addProjectToSolution = async (
    componentUuid: string,
    projectDescription: string,
    projectRole: string
  ): Promise<void> => {
    const res: any = await addProject({
      ProjectUuid: componentUuid,
      SolutionUuid: solution.Uuid,
      Description: projectDescription,
      Role: projectRole,
    });

    if ("data" in res) {
      const sol = res.data.solution as SolutionType;
      const currentComponent = sol.Components.find(
        (c) => c.ItemUuid === componentUuid
      );
      toast.success(
        `Project "${currentComponent?.Item?.Label}" added to solution successfully`
      );
      setSelectedProject("");
      setSolution(sol);

      if (sol?.Components) {
        for (const component of sol.Components) {
          if (component.Bag.External && "Component" in component.Bag.External) {
            for (const componentUuidValue of Object.values(
              component.Bag.External.Component as Record<string, string>
            )) {
              if (
                sol.Components.some((c) => c.ItemUuid === componentUuidValue)
              ) {
                continue;
              }
              await addProjectToSolution(
                componentUuidValue,
                "Component dependency",
                "dependency"
              );
            }
          }
        }
      }
    }

    if ("error" in res && res.error?.data?.errors) {
      toast.error(
        "Error adding project to solution: " +
          JSON.stringify(res.error.data.errors)
      );
    }
  };

  return (
    <>
      <div className="">
        <div className="text-secondary my-2">
          Components must be set as "solution" and "production" in the component
          configuration.Components must also match correct variable interface
          for each role.
        </div>
        <div className=" me-3">
          <div className="fw-bold my-1">Select a Component:</div>
          <Input
            inputType="select"
            value={[selectedProject]}
            onChange={([value]: string[]) => {
              const project = projects.find(
                (projectItem) => projectItem.UUID === value
              );
              const projectSupportedRoles = project
                ? getProjectSupportedRoles(project)
                : [];

              setSelectedProject(value);
              setSelectedRoles(
                projectSupportedRoles
                  .filter((supportedRole) => !supportedRole.alreadyAdded)
                  .map((supportedRole) => supportedRole.value)
              );
              setRoleDescriptions(
                projectSupportedRoles
                  .filter((supportedRole) => !supportedRole.alreadyAdded)
                  .reduce<Record<string, string>>(
                    (descriptions, supportedRole) => {
                      descriptions[supportedRole.value] =
                        supportedRole.description;
                      return descriptions;
                    },
                    {}
                  )
              );
            }}
            options={projects?.map((project) => {
              const projectSupportedRoleLabels = getProjectSupportedRoles(
                project
              ).map((supportedRole) => supportedRole.label);

              return {
                label: projectSupportedRoleLabels.length
                  ? `${project.Label} (${projectSupportedRoleLabels.join(
                      ", "
                    )})`
                  : project.Label,
                value: project.UUID,
              };
            })}
          />
        </div>
        {selectedProjectDetails && (
          <div className="me-3 mt-3">
            <div className="fw-bold my-1">Supported Roles:</div>
            <div className="d-flex flex-column gap-2">
              {supportedRoles.map((supportedRole) => (
                <Form.Check
                  key={supportedRole.value}
                  type="checkbox"
                  id={`supported-role-${supportedRole.value}`}
                  label={
                    <span>
                      <span>{supportedRole.label}</span>
                      <span className="text-secondary ms-1">
                        - {supportedRole.description}
                      </span>
                      {supportedRole.interfaceVars.length > 0 && (
                        <span className="text-secondary ms-1">
                          (Required variable roles:{" "}
                          {supportedRole.interfaceVars.map((interfaceVar) => (
                            <span
                              key={interfaceVar.value}
                              className={
                                interfaceVar.supported
                                  ? "text-success ms-1"
                                  : "text-danger ms-1"
                              }
                            >
                              {interfaceVar.supported ? "✓" : "✕"}{" "}
                              {interfaceVar.value}
                            </span>
                          ))}
                          )
                        </span>
                      )}
                      {supportedRole.alreadyAdded && (
                        <span className="text-secondary ms-1">
                          - Already added for this component
                        </span>
                      )}
                    </span>
                  }
                  checked={
                    supportedRole.alreadyAdded ||
                    selectedRoles.includes(supportedRole.value)
                  }
                  disabled={!supportedRole.supported || supportedRole.alreadyAdded}
                  onChange={(event) =>
                    toggleSelectedRole(
                      supportedRole.value,
                      event.target.checked
                    )
                  }
                />
              ))}
            </div>
          </div>
        )}
        {selectedSupportedRoles.length > 0 && (
          <div className="me-3 mt-3">
            <div className="fw-bold my-1">Role Descriptions:</div>
            {selectedSupportedRoles.map((supportedRole) => (
              <div key={supportedRole.value} className="mb-3">
                <div className="fw-bold my-1">{supportedRole.label}</div>
                <Input
                  inputType="textArea"
                  placeholder={`${supportedRole.label} Description`}
                  value={[
                    roleDescriptions[supportedRole.value] ||
                      defaultRoleDescriptions[supportedRole.value] ||
                      "",
                  ]}
                  onChange={([value]: string[]) =>
                    setSelectedRoleDescription(supportedRole.value, value)
                  }
                />
              </div>
            ))}
          </div>
        )}

        <div className="">
          <div className="my-3 d-flex justify-content-end align-items-center gap-2">
            <Button
              disabled={
                !selectedProject ||
                selectedSupportedRoles.length === 0 ||
                !selectedRoleDescriptionsComplete ||
                solution.Status === "public" ||
                adding
              }
              className="btn-colored svg-white rounded-2"
              variant=""
              onClick={async () => {
                setAdding(true);
                const componentUuid = selectedProject;
                const rolesToAdd = selectedSupportedRoles.map(
                  (supportedRole) => ({
                    description: (
                      roleDescriptions[supportedRole.value] ||
                      defaultRoleDescriptions[supportedRole.value]
                    ).trim(),
                    value: supportedRole.value,
                  })
                );

                for (const selectedRole of rolesToAdd) {
                  await addProjectToSolution(
                    componentUuid,
                    selectedRole.description,
                    selectedRole.value
                  );
                }

                setSelectedRoles([]);
                setRoleDescriptions({});

                setTimeout(() => {
                  setAdding(false);
                }, 700);
              }}
            >
              <AddIcon width={15} className="me-2" />
              Add component
              {adding && (
                <FontAwesomeIcon icon={faSpinner} spin className="ms-2" />
              )}
            </Button>
          </div>
        </div>
      </div>
      {solution?.Components && (
        <div className="mt-3">
          <h5 className="fw-bold my-3">Current Components in Solution:</h5>
          <Table striped bordered hover size="sm">
            <thead>
              <tr>
                <th>Label</th>
                <th>Depends on</th>
                <th>Description</th>
                <th>Status</th>
                <th>Version</th>
                <th>Role</th>
                <th>Role Description</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {solution.Components?.map((component) => (
                <tr key={component.ComponentUuid}>
                  <td>{component.Item.Label}</td>
                  <td>
                    {component.Bag.External?.Component
                      ? Object.values(component.Bag.External.Component)
                          .map(
                            (uuid) =>
                              solution.Components?.find(
                                (c) => c.ItemUuid === uuid
                              )?.Item.Label || "Unknown"
                          )
                          .join(", ")
                      : "-"}
                  </td>
                  <td>{component.Item.Description}</td>
                  <td>{component.Item.Status}</td>
                  <td>{component.Item.Version}</td>
                  <td>{component.Role}</td>
                  <td>{component.Description}</td>
                  <td>
                    <Button
                      className="text-danger"
                      variant=""
                      type="button"
                      disabled={
                        solution.Components?.some((c) =>
                          Object.values(c.Bag.External?.Component || {}).some(
                            (uuid) => uuid === component.ItemUuid
                          )
                        ) ||
                        solution.Status === "public" ||
                        removing
                      }
                      onClick={() => {
                        setRemoving(true);
                        removeProject({
                          ComponentUuid: component.ComponentUuid,
                          SolutionUuid: solution.Uuid,
                        }).then((res: any) => {
                          if ("data" in res) {
                            toast.success(
                              "Project removed from solution successfully"
                            );
                          }
                          if ("error" in res && res.error?.data?.errors) {
                            toast.error(
                              `Error removing project from solution: ${JSON.stringify(
                                res.error.data.errors
                              )}`
                            );
                          }
                          setTimeout(() => {
                            setRemoving(false);
                          }, 700);
                        });
                      }}
                    >
                      <RemoveIcon width={15} className="me-2" />
                      Remove
                      {removing && (
                        <FontAwesomeIcon
                          icon={faSpinner}
                          spin
                          className="ms-2"
                        />
                      )}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </>
  );
};

export default SelectSolutionProject;
